import { Download, Printer } from 'lucide-react'
import { Button, MultiSelect, DatePicker } from '@atrvd/saas-ui'
import { downloadTransactionsCsv } from '@/lib/exportCsv'
import { toLocalDateString, safeParseDate } from '@/lib/date-utils'
import type { BankingFilters as BankingFiltersType, Facility, BankTransaction } from '@/types/banking'

interface BankingFiltersProps {
  filters: BankingFiltersType
  onFiltersChange: (filters: BankingFiltersType) => void
  facilities: Facility[]
  /** Dynamic account options — { value: plaid_accounts.id, label: display name } */
  accountOptions: Array<{ value: string; label: string }>
  transactions: BankTransaction[]
  /** Dynamic category options fetched from the database */
  categoryOptions: Array<{ value: string; label: string }>
}

/**
 * Banking filters component with period selector, date range picker, dropdowns, and action buttons
 */
export function BankingFilters({
  filters,
  onFiltersChange,
  facilities,
  accountOptions,
  transactions,
  categoryOptions,
}: BankingFiltersProps) {
  return (
    <div className="flex flex-wrap items-end gap-4">
      {/* From Date */}
      <div className="flex flex-col gap-1">
        <label id="from-date-label" className="text-sm font-medium text-muted-foreground">From</label>
        <DatePicker
          aria-labelledby="from-date-label"
          date={safeParseDate(filters.startDate)}
          onDateChange={(date) => {
            if (!date) {
              onFiltersChange({ ...filters, startDate: undefined })
              return
            }
            const dateStr = toLocalDateString(date)
            // If From is after To, move To to match From
            const endDate = filters.endDate && dateStr > filters.endDate ? dateStr : filters.endDate
            onFiltersChange({ ...filters, startDate: dateStr, endDate })
          }}
          placeholder="Start date"
        />
      </div>

      {/* To Date */}
      <div className="flex flex-col gap-1">
        <label id="to-date-label" className="text-sm font-medium text-muted-foreground">To</label>
        <DatePicker
          aria-labelledby="to-date-label"
          date={safeParseDate(filters.endDate)}
          onDateChange={(date) => {
            if (!date) {
              onFiltersChange({ ...filters, endDate: undefined })
              return
            }
            const dateStr = toLocalDateString(date)
            // If To is before From, move From to match To
            const startDate = filters.startDate && dateStr < filters.startDate ? dateStr : filters.startDate
            onFiltersChange({ ...filters, startDate, endDate: dateStr })
          }}
          placeholder="End date"
        />
      </div>

      {/* Facility */}
      <div className="flex flex-col gap-1">
        <label className="text-sm font-medium text-muted-foreground">Facility</label>
        <MultiSelect
          options={facilities.map((f) => ({ value: f.id, label: f.name }))}
          value={filters.facilityIds || []}
          onChange={(value) => onFiltersChange({ ...filters, facilityIds: value.length > 0 ? value : undefined })}
          placeholder="All Facilities"
          className="w-48"
          searchable
          searchPlaceholder="Search facilities..."
        />
      </div>

      {/* Account */}
      <div className="flex flex-col gap-1">
        <label className="text-sm font-medium text-muted-foreground">Account</label>
        <MultiSelect
          options={accountOptions}
          value={filters.accountIds || []}
          onChange={(value) => onFiltersChange({ ...filters, accountIds: value.length > 0 ? value : undefined })}
          placeholder="All Accounts"
          className="w-52"
          searchable
          searchPlaceholder="Search accounts..."
        />
      </div>

      {/* Category */}
      <div className="flex flex-col gap-1">
        <label className="text-sm font-medium text-muted-foreground">Category</label>
        <MultiSelect
          options={categoryOptions}
          value={filters.categories || []}
          onChange={(value) => onFiltersChange({ ...filters, categories: value.length > 0 ? value : undefined })}
          placeholder="All Categories"
          className="w-48"
          searchable
          searchPlaceholder="Search categories..."
        />
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Action Buttons */}
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="icon"
          onClick={() => downloadTransactionsCsv(transactions)}
          disabled={transactions.length === 0}
          title={transactions.length === 0 ? 'No data to export' : 'Download CSV'}
          aria-label="Download transactions"
        >
          <Download className="h-4 w-4" />
        </Button>
        <Button variant="outline" size="icon" disabled title="Coming soon" aria-label="Print transactions">
          <Printer className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}
